import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconTemplate } from '@components/icons/template';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { AIBuilderExamplePrompt } from 'api/ai-builder/requests';

type WorkflowExamplesPanelProps = {
  isOpen: boolean;
  examplePrompts: AIBuilderExamplePrompt[];
  onClose: () => void;
  onSelectExamplePrompt: (prompt: string) => void;
};

export const WorkflowExamplesPanel = ({
  isOpen,
  examplePrompts,
  onClose,
  onSelectExamplePrompt,
}: WorkflowExamplesPanelProps) => {
  const { t } = useScopedTranslate(locale);
  const [exampleSearch, setExampleSearch] = useState('');

  const filteredExamplePrompts = useMemo(() => {
    const trimmedQuery = exampleSearch.trim().toLowerCase();
    if (!trimmedQuery) return examplePrompts;

    return examplePrompts.filter((examplePrompt) => {
      return (
        examplePrompt.label.toLowerCase().includes(trimmedQuery) ||
        examplePrompt.description.toLowerCase().includes(trimmedQuery) ||
        examplePrompt.prompt.toLowerCase().includes(trimmedQuery)
      );
    });
  }, [examplePrompts, exampleSearch]);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <ExamplesPanel
          key="workflow-examples-view"
          initial={{ x: pxToRem(-24), opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: pxToRem(-24), opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: 'easeOut',
          }}
        >
          <ExamplesHeader>
            <Stack orientation="horizontal" space={2} align="center">
              <IconTemplate color="elementBlue100" />
              <Text font="bold18" text={t('workflowExamplesTitle')} />
            </Stack>
            <IconButton
              icon={<IconCloseRegular />}
              onClick={() => {
                setExampleSearch('');
                onClose();
              }}
              variant="ghost-purple"
              size="md"
            />
          </ExamplesHeader>
          <ExamplesContent>
            <SearchBox
              placeholder={t('exampleSearchPlaceholder')}
              value={exampleSearch}
              onChange={setExampleSearch}
            />
            <ExampleList>
              {filteredExamplePrompts.map((examplePrompt) => (
                <Card
                  key={examplePrompt.shortId}
                  interactive
                  variant="default"
                  space={3}
                  onClick={() => {
                    setExampleSearch('');
                    onSelectExamplePrompt(examplePrompt.prompt);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setExampleSearch('');
                      onSelectExamplePrompt(examplePrompt.prompt);
                    }
                  }}
                >
                  <Stack space={1}>
                    <Text font="semibold12" text={examplePrompt.label} />
                    <Text font="regular12" color="textSecondary" text={examplePrompt.description} />
                  </Stack>
                </Card>
              ))}
              {filteredExamplePrompts.length === 0 && (
                <NoResultsContainer>
                  <Text font="regular12" color="textSecondary" text={t('noMatchingExamples')} />
                </NoResultsContainer>
              )}
            </ExampleList>
          </ExamplesContent>
        </ExamplesPanel>
      )}
    </AnimatePresence>
  );
};

const ExamplesPanel = styled(motion.div)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  minHeight: 0,
  position: 'absolute',
  top: 0,
  left: `calc(100% + ${pxToRem(8)})`,
  background: theme.color.backgroundLightDark,
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.color.border10}`,
  boxShadow: theme.shadow.medium,
  overflow: 'hidden',
});

const ExamplesHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})({
  borderBottom: `1px solid ${theme.color.border10}`,
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
});

const ExamplesContent = styled(Stack).attrs({
  space: 3,
})({
  flex: '1 1 auto',
  minHeight: 0,
  padding: pxToRem(16),
});

const ExampleList = styled(Stack).attrs({
  space: 3,
})({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: pxToRem(2),
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const NoResultsContainer = styled.div({
  padding: `${pxToRem(8)} ${pxToRem(4)}`,
});

const locale = {
  en: {
    workflowExamplesTitle: 'Workflow examples',
    exampleSearchPlaceholder: 'Search...',
    noMatchingExamples: 'No matching examples',
  },
};
